import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Save, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CATEGORY_KEYS, groupByCategory } from "../hooks/useMenu";
import HoursManager from "../components/HoursManager";
import ContactManager from "../components/ContactManager";
import ImagesManager from "../components/ImagesManager";
import AccountManager from "../components/AccountManager";

const CAT_LABELS = {
  coffee: "Caffè & Bevande",
  breakfast: "Colazione",
  lunch: "Pranzo & Cena",
  dessert: "Dolci",
};

const empty = {
  category: "coffee",
  name_it: "",
  name_en: "",
  name_de: "",
  desc_it: "",
  desc_en: "",
  desc_de: "",
  price: "",
  order: 0,
};

const AdminPage = () => {
  const { user, loading, logout, authedAxios, applyAuthResponse } = useAuth();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [editing, setEditing] = useState(null); // item or 'new'
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        setFetching(true);
        const res = await authedAxios().get("/menu/items");
        setItems(res.data);
      } catch (e) {
        toast.error("Errore caricamento menu");
      } finally {
        setFetching(false);
      }
    };
    load();
  }, [user, authedAxios]);

  if (loading) return <div className="pt-40 text-center">…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const grouped = groupByCategory(items);

  const startNew = () => {
    setForm(empty);
    setEditing("new");
  };

  const startEdit = (item) => {
    setForm({ ...item });
    setEditing(item.id);
  };

  const cancel = () => {
    setEditing(null);
    setForm(empty);
  };

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const save = async (e) => {
    e?.preventDefault();
    const required = ["category", "name_it", "name_en", "name_de", "price"];
    for (const r of required) {
      if (!String(form[r] ?? "").trim()) {
        toast.error(`Campo obbligatorio: ${r}`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        category: form.category,
        name_it: form.name_it.trim(),
        name_en: form.name_en.trim(),
        name_de: form.name_de.trim(),
        desc_it: (form.desc_it || "").trim(),
        desc_en: (form.desc_en || "").trim(),
        desc_de: (form.desc_de || "").trim(),
        price: form.price.trim(),
        order: Number(form.order) || 0,
      };
      const ax = authedAxios();
      if (editing === "new") {
        const res = await ax.post("/menu/items", payload);
        setItems((s) => [...s, res.data]);
        toast.success("Piatto creato");
      } else {
        const res = await ax.put(`/menu/items/${editing}`, payload);
        setItems((s) => s.map((i) => (i.id === editing ? res.data : i)));
        toast.success("Piatto aggiornato");
      }
      cancel();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore salvataggio";
      toast.error(typeof msg === "string" ? msg : "Errore");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo piatto?")) return;
    try {
      await authedAxios().delete(`/menu/items/${id}`);
      setItems((s) => s.filter((i) => i.id !== id));
      toast.success("Eliminato");
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <main data-testid="admin-page" className="min-h-screen pt-40 pb-20 bg-[color:var(--ivory)]">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="overline text-[color:var(--wine)] flex items-center">
              <span className="section-line"></span>
              Pannello amministrazione
            </p>
            <h1 className="mt-6 font-serif-display text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Gestione menu.
            </h1>
            <p className="mt-3 text-sm text-[color:var(--ink-3)]">
              {user.email}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={startNew}
              data-testid="admin-add-item"
              className="btn-primary"
              style={{ padding: "0.7rem 1.2rem" }}
            >
              <Plus size={14} strokeWidth={1.6} />
              Nuovo piatto
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="btn-ghost"
              style={{ padding: "0.7rem 1.2rem" }}
            >
              <LogOut size={14} strokeWidth={1.6} />
              Esci
            </button>
          </div>
        </div>

        {editing && (
          <form
            onSubmit={save}
            data-testid="admin-edit-form"
            className="mb-12 bg-[color:var(--ivory-2)] p-6 lg:p-8 border border-[color:var(--line)]"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif-display text-3xl">
                {editing === "new" ? "Nuovo piatto" : "Modifica piatto"}
              </h3>
              <button type="button" onClick={cancel} className="p-2">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="overline text-[color:var(--ink-3)]">Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm((s) => ({ ...s, category: v }))}>
                  <SelectTrigger
                    data-testid="form-category"
                    className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus:ring-0 px-0 text-lg"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[color:var(--ivory)] border border-[color:var(--line)] rounded-none">
                    {CATEGORY_KEYS.map((c) => (
                      <SelectItem key={c} value={c}>{CAT_LABELS[c]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-[color:var(--ink-3)]">Prezzo</Label>
                <Input
                  data-testid="form-price"
                  value={form.price}
                  onChange={update("price")}
                  placeholder="€9,95"
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-lg"
                />
              </div>
              <div>
                <Label className="overline text-[color:var(--ink-3)]">Nome (IT)</Label>
                <Input data-testid="form-name-it" value={form.name_it} onChange={update("name_it")}
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-lg" />
              </div>
              <div>
                <Label className="overline text-[color:var(--ink-3)]">Nome (EN)</Label>
                <Input data-testid="form-name-en" value={form.name_en} onChange={update("name_en")}
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-lg" />
              </div>
              <div>
                <Label className="overline text-[color:var(--ink-3)]">Nome (DE)</Label>
                <Input data-testid="form-name-de" value={form.name_de} onChange={update("name_de")}
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-lg" />
              </div>
              <div>
                <Label className="overline text-[color:var(--ink-3)]">Ordine</Label>
                <Input data-testid="form-order" type="number" value={form.order} onChange={update("order")}
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-lg" />
              </div>
              <div className="md:col-span-2">
                <Label className="overline text-[color:var(--ink-3)]">Descrizione (IT)</Label>
                <Textarea data-testid="form-desc-it" rows={2} value={form.desc_it} onChange={update("desc_it")}
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-base resize-none" />
              </div>
              <div className="md:col-span-2">
                <Label className="overline text-[color:var(--ink-3)]">Descrizione (EN)</Label>
                <Textarea data-testid="form-desc-en" rows={2} value={form.desc_en} onChange={update("desc_en")}
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-base resize-none" />
              </div>
              <div className="md:col-span-2">
                <Label className="overline text-[color:var(--ink-3)]">Descrizione (DE)</Label>
                <Textarea data-testid="form-desc-de" rows={2} value={form.desc_de} onChange={update("desc_de")}
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-base resize-none" />
              </div>
            </div>
            <div className="mt-8 flex gap-3">
              <button type="submit" disabled={saving} data-testid="form-save" className="btn-primary disabled:opacity-50">
                <Save size={14} strokeWidth={1.6} />
                {saving ? "Salvataggio..." : "Salva"}
              </button>
              <button type="button" onClick={cancel} className="btn-ghost">
                Annulla
              </button>
            </div>
          </form>
        )}

        {fetching ? (
          <p className="text-[color:var(--ink-3)]">Caricamento...</p>
        ) : (
          <>
            <div className="mb-16 pb-12 border-b border-[color:var(--line)]">
              <AccountManager authedAxios={authedAxios} user={user} onUpdated={applyAuthResponse} />
            </div>
            <div className="mb-16 pb-12 border-b border-[color:var(--line)]">
              <HoursManager authedAxios={authedAxios} />
            </div>
            <div className="mb-16 pb-12 border-b border-[color:var(--line)]">
              <ContactManager authedAxios={authedAxios} />
            </div>
            <div className="mb-16 pb-12 border-b border-[color:var(--line)]">
              <ImagesManager authedAxios={authedAxios} />
            </div>
            {CATEGORY_KEYS.map((cat) => (
            <section key={cat} data-testid={`admin-cat-${cat}`} className="mb-14">
              <p className="overline text-[color:var(--wine)] flex items-center mb-6">
                <span className="section-line"></span>
                {CAT_LABELS[cat]}
                <span className="ml-3 text-[color:var(--ink-3)]">({grouped[cat].length})</span>
              </p>
              <div className="space-y-1">
                {grouped[cat].length === 0 && (
                  <p className="py-4 text-[color:var(--ink-3)] text-sm">Nessun piatto in questa categoria.</p>
                )}
                {grouped[cat].map((item) => (
                  <div
                    key={item.id}
                    data-testid={`menu-row-${item.id}`}
                    className="flex items-start justify-between gap-4 py-4 border-b border-[color:var(--line)]"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <h4 className="font-serif-display text-2xl">{item.name_it}</h4>
                        <span className="font-serif-display text-lg text-[color:var(--wine)] whitespace-nowrap">
                          {item.price}
                        </span>
                        <span className="overline text-[color:var(--ink-3)] text-[0.6rem]">#{item.order}</span>
                      </div>
                      <p className="mt-1 text-sm text-[color:var(--ink-3)]">{item.desc_it}</p>
                      <p className="text-xs text-[color:var(--ink-3)]/70 italic">{item.name_en} · {item.name_de}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => startEdit(item)}
                        data-testid={`edit-${item.id}`}
                        className="p-2 hover:text-[color:var(--wine)]"
                        title="Modifica"
                      >
                        <Pencil size={16} strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => remove(item.id)}
                        data-testid={`delete-${item.id}`}
                        className="p-2 hover:text-[color:var(--wine)]"
                        title="Elimina"
                      >
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
          </>
        )}
      </div>
    </main>
  );
};

export default AdminPage;
